/*
 * Les listes fermées de Nextilt, écrites une fois (chantier nettoyage-contrat, ticket 01).
 *
 * SQLite ne connaît pas les enums : ces valeurs sont des String en base. C'est ici, et
 * uniquement ici, qu'elles forment un ensemble fermé ; le serveur, l'app et le MCP les lisent
 * ici. L'écran en dit les mots (`client/src/partage/vocabulaire.ts`), jamais les valeurs.
 */
/** Où en est une bulle. */
export const ETATS = ['a_faire', 'en_attente_confirmation', 'fait', 'sans_objet'];
/**
 * Les choix de « J'ai », le temps qu'on a devant soi dans « Maintenant » : 10 min, 30 min, 1 h,
 * une demi-journée, une journée. Ce n'est plus la durée d'une tâche, gardée en minutes
 * (`effortMin`, chantier duree-a-la-minute, ticket 01). Ce sont aussi les cinq anciens mots de
 * cette durée, qu'un plan ou un modèle peut encore porter : `MINUTES_DE_J_AI` les convertit.
 */
export const TEMPS_DE_J_AI = ['dix_min', 'trente_min', 'une_heure', 'demi_journee', 'journee'];
/**
 * Chaque choix de « J'ai », en minutes : une demi-journée vaut quatre heures, la journée sept
 * (la même échelle que les temps proposés d'une journée, et la conversion de la base faite par
 * la migration `20261011000000_duree_a_la_minute`).
 */
export const MINUTES_DE_J_AI = Object.freeze({
    dix_min: 10,
    trente_min: 30,
    une_heure: 60,
    demi_journee: 240,
    journee: 420,
});
/** Sans bouger, ou il faut y aller. */
export const CANAUX = ['sans_bouger', 'il_faut_y_aller'];
/**
 * Ce que l'utilisateur peut choisir comme importance (chantier `priorites`, ADR 0011) ; `null`
 * vaut « non réglée » (plus « Auto » depuis le chantier `flottaison`). « En attente » vient de
 * l'attente, jamais d'un choix. L'importance présentée se lit dans `server/src/domain/moteur/priorites/importance.ts`.
 */
export const CHOIX_DE_PRIORITE = ['urgent', 'important', 'plus_tard'];
/**
 * Les cinq thèmes de dossier. Attribués en tourniquet (SPEC §3), sauf quand on choisit la
 * couleur d'un projet en le créant.
 */
export const COULEURS = ['dossier-1', 'dossier-2', 'dossier-3', 'dossier-4', 'dossier-5'];
/**
 * Le nom de chaque couleur, celui que l'écran dit (le + de « Tous les projets ») et que Claude
 * entend : écrits ici une fois, pour l'app et le MCP (chantier nettoyage-mcp, ticket 03).
 */
export const NOMS_DES_COULEURS = {
    'dossier-1': 'Bleu-vert',
    'dossier-2': 'Ocre',
    'dossier-3': 'Violet',
    'dossier-4': 'Vert',
    'dossier-5': 'Terracotta',
};
/**
 * Les couleurs d'une branche, choisies bulle par bulle (chantier habit-des-bulles, retour d'Igor
 * du 2026-10-21 : « il en faut plus si il y a beaucoup de bulle autours de la mere ») : les cinq
 * des projets, puis sept de plus. Un projet garde la sienne parmi les cinq.
 */
export const COULEURS_DE_BULLE = [
    ...COULEURS,
    'teinte-6',
    'teinte-7',
    'teinte-8',
    'teinte-9',
    'teinte-10',
    'teinte-11',
    'teinte-12',
];
/** Le nom de chaque couleur d'une branche, tel que l'écran le dit. */
export const NOMS_DES_COULEURS_DE_BULLE = {
    ...NOMS_DES_COULEURS,
    'teinte-6': 'Bleu',
    'teinte-7': 'Rouge',
    'teinte-8': 'Rose',
    'teinte-9': 'Moutarde',
    'teinte-10': 'Olive',
    'teinte-11': 'Ardoise',
    'teinte-12': 'Brun',
};
/*
 * L'habit d'un projet (chantier habit-du-projet, ticket 01, décisions d'Igor du 2026-10-20) : la
 * forme de ses bulles, leur trait et le fond de sa carte. Seul un projet en a un, comme seul un
 * projet a une couleur ; `null` en base vaut le défaut (`habitDuProjet`, `habit.ts`).
 */
/** Galet, Rond, Étiquette, Ticket, Fiche bristol, Post-it, Bout de scotch, Rectangle. */
export const FORMES_DE_PROJET = ['galet', 'rond', 'etiquette', 'ticket', 'bristol', 'postit', 'scotch', 'rectangle'];
/**
 * Les quatorze bulles-objets, des repères visuels en silhouette (chantier habit-des-bulles,
 * modification 01, maquette `formes-de-bulle.html`) : Ampoule, Téléphone, Ordinateur, Dossier,
 * Fichier, Enveloppe, Calendrier, Panier, Boîte à outils, Maison, Billet, Horloge, Nuage, Drapeau.
 */
export const OBJETS_DE_BULLE = [
    'ampoule',
    'telephone',
    'ordinateur',
    'dossier',
    'fichier',
    'enveloppe',
    'calendrier',
    'panier',
    'outils',
    'maison',
    'billet',
    'horloge',
    'nuage',
    'drapeau',
];
/**
 * La forme d'une bulle, réglée bulle par bulle (chantier habit-des-bulles, modification 01) : les
 * huit du projet, puis les quatorze objets. Un projet, lui, garde sa forme parmi les huit.
 */
export const FORMES_DE_BULLE = [...FORMES_DE_PROJET, ...OBJETS_DE_BULLE];
/** Fin, Gros, Feutre, Craie, Crayon. */
export const TRAITS_DE_PROJET = ['fin', 'gros', 'feutre', 'craie', 'crayon'];
/** Uni, Lignes, Carreaux, Bristol, Post-it : les cinq papiers des fiches (Igor, 2026-10-21 : « les 5 pas juste 3 »). */
export const FONDS_DE_PROJET = ['uni', 'lignes', 'carreaux', 'bristol', 'postit'];
/**
 * L'angle sur lequel un projet s'ouvre (chantier angle-a-l-ouverture, modification 01) : Carte,
 * Liste, Hiérarchie. Maintenant et Agenda ne sont pas des angles d'ouverture. Rangé avec l'habit :
 * seul un projet en a un ; `null` en base vaut Carte (`habitDuProjet`).
 */
export const ANGLES_D_OUVERTURE = ['carte', 'liste', 'hierarchie'];
/**
 * Le mode d'une feuille (chantier modes-de-la-carte, décisions d'Igor du 2026-10-21) : le
 * préréglage des touches de sa Carte et de sa Hiérarchie, à l'ordinateur. De base, Réflexion,
 * Exécution, Analyse (Analyse vient avec le chantier `theme-analyse`). Toute bulle peut avoir le
 * sien ; `null` en base vaut « comme la mère », et De base tout en haut (`modeDeLaFeuille`).
 */
export const MODES_DE_LA_CARTE = ['de-base', 'reflexion', 'execution', 'analyse'];
/*
 * Le type et le papier d'une fiche (chantier types-de-fiche, modification 01) : le type dit ce que
 * la fiche met en avant, le papier son allure. Une Note, une Contact, une Plan portent le papier de
 * leur type ; seule une Classique choisit le sien (chantier reglages-de-la-fiche, modification 01,
 * `PAPIER_DU_TYPE`). Toute bulle peut choisir les siens, un projet donne ceux de ses fiches par
 * défaut ; `null` en base passe au suivant (`ficheDeLaBulle`, `fiche.ts`).
 */
/** Classique, Note, Contact, Plan. */
export const TYPES_DE_FICHE = ['classique', 'note', 'contact', 'plan'];
/**
 * Feuille blanche, Lignes, Carreaux, Bristol. Le Post-it est parti (reglages-de-la-fiche, 01) : un
 * `postit` resté en base n'est pas transformé, il se lit comme rien de choisi.
 */
export const PAPIERS_DE_FICHE = ['blanche', 'lignes', 'carreaux', 'bristol'];
/**
 * Les deux sortes de papiers posés sur la carte (chantier post-it-et-listes, ticket 01) : le
 * post-it (un titre facultatif, un texte libre) et la liste libre (un titre, des lignes à cocher).
 * Un papier n'est pas une bulle : le moteur ne le lit jamais.
 */
export const SORTES_DE_PAPIER = ['post_it', 'liste'];
/**
 * D'où vient une note de la boîte d'entrée (chantier boite-d-entree, ticket 01) : `toi` par la
 * session, `claude` par une clé d'accès ou le connecteur. Lue à la porte de la requête, jamais
 * dans ce qu'elle envoie.
 */
export const ORIGINES_DE_NOTE = ['toi', 'claude'];
/**
 * Les trois sortes d'un lien entre deux bulles (chantier liens-entre-bulles, ticket 01) : la cible
 * attend la source (`attend`, la seule qui soit une condition pour le moteur), la source sert à la
 * cible (`sert_a`), ou les deux se touchent, sans sens (`touche_a`).
 */
export const SORTES_DE_LIEN = ['attend', 'sert_a', 'touche_a'];
/** Le papier dont il est fait : post-it jaune, post-it vert, feuille à lignes, feuille à carreaux. */
export const FONDS_DE_PAPIER = ['jaune', 'vert', 'lignes', 'carreaux'];
/**
 * Les feuilles du profil qui portent des lignes libres (chantier profil, ticket 01) : Qui je suis,
 * Ce que j'ai, Ma façon de faire, dans l'ordre de la maquette.
 */
export const FEUILLES_DE_PROFIL = ['qui-je-suis', 'ce-que-j-ai', 'ma-facon-de-faire'];
/**
 * Les treize champs du profil, dans l'ordre des feuilles : Qui je suis (prénom, ville, code postal
 * ou quartier, ce que tu fais, ce vers quoi tu vas, où tu travailles, tes heures de travail), Ce que
 * j'ai (permis, voiture, comment tu te déplaces), Ma façon de faire (quand tu as du temps, ton
 * énergie en semaine, ton meilleur moment). Tous facultatifs, du texte court. Le quartier, une
 * adresse approchée (choix C d'Igor du 2026-10-21) : la rue et le numéro iront dans Sensible. Le
 * lieu et les heures de travail, le déplacement, le meilleur moment, à la suite des autres de leur
 * feuille (chantier profil-pour-claude, tickets 01 et 02) : ce que Claude doit connaître pour « Je
 * fais quoi, là ? » et « Prépare ma journée ».
 */
export const CHAMPS_DU_PROFIL = [
    'prenom',
    'ville',
    'quartier',
    'situation',
    'enRouteVers',
    'lieuDeTravail',
    'heuresDeTravail',
    'permis',
    'voiture',
    'deplacement',
    'rythme',
    'courage',
    'meilleurMoment',
];
/**
 * L'énergie en semaine, choisie en trois boutons (retour d'Igor au test du chantier profil,
 * 2026-10-21) : gardée comme le texte du bouton, dans le champ `courage`.
 */
export const ENERGIES_DU_PROFIL = ['Peu', 'Moyenne', 'Beaucoup'];
/** Où l'on est, pour « Maintenant » : chez moi, ou dehors. */
export const LIEUX = ['chez_moi', 'dehors'];
/** Le courage du moment, pour « Maintenant » : le k de la flottaison. */
export const COURAGES = ['d_attaque', 'normal', 'fatigue'];
/** Où ça se fait, le jour préparé : les deux, depuis chez moi (`sans_bouger`), je peux sortir. */
export const OU = ['tout', 'sans_bouger', 'il_faut_y_aller'];
/**
 * Pourquoi une tâche ne se prévoit pas un jour (le refus `pas_possible_ce_jour`, chantier
 * nettoyage-chemin, ticket 04) : faite (Acquis), En réflexion, En instruction (elle attend le
 * monde), bloquée, elle a encore à faire dedans, c'est un rendez-vous. Le moteur la calcule
 * (`raisonDeNePasSePrevoir`, `server/src/domain/maintenant/journee/journee.ts`) ; le MCP en dit les mots.
 */
export const RAISONS_DE_NE_PAS_SE_PREVOIR = [
    'faite',
    'en_reflexion',
    'en_instruction',
    'bloquee',
    'a_faire_dedans',
    'rendez_vous',
];
/** Les temps proposés pour préparer un jour, en minutes : 30 min, 1 h, 2 h, 3 h, une demi-journée, toute la journée. */
export const TEMPS_PROPOSES = [30, 60, 120, 180, 240, 420];
/**
 * Les réglages d'une journée quand rien d'autre ne les dit, écrits une fois (chantier
 * nettoyage-journee, ticket 04, décision d'Igor du 2026-10-07 : 2 h partout, le + compris) : 2 h,
 * dès 9 h, chez moi ou dehors, Normal. Le serveur les donne à un jour vide (« Garder pour
 * aujourd'hui », qui commence maintenant) ; le panneau de l'ordinateur, la question du téléphone
 * et le + en partent.
 */
export const JOURNEE_PAR_DEFAUT = Object.freeze({ tempsMin: 120, debut: '09:00', ou: 'tout', courage: 'normal' });
